<template>
    <div v-show="initDone">
        <div class="columns">
            <div class="column">
                <div v-if="!store2.publishingMode">
                    <p class="mb-2">{{ translate("page.admin_academy_results.choose_criteria") }}</p>

                    <AcademyResultsOptions class="mb-2" />

                    <div class="buttons">
                        <button class="button is-success" @click="openAddRuleModal">
                            <span class="icon">
                                <i class="fa fa-plus" />
                            </span>
                            <span>{{ translate("page.admin_academy_results.add_rule_btn") }}</span>
                        </button>

                        <LoadingButton :is-loading="loadingInProgress" class="button" @click="refreshResults" :disabled="!refreshPossible">
                            <span class="icon">
                                <i class="fas fa-sync-alt" />
                            </span>
                            <span>{{ translate("common.refresh") }}</span>
                        </LoadingButton>

                        <a class="button" :href="getExportCsvUrl()">
                            <span class="icon">
                                <i class="fa fa-download" />
                            </span>
                            <span>
                                {{ translate("admin_payments.export_csv") }}
                            </span>
                        </a>

                        <button class="button is-primary" :disabled="!publishingPossible" @click="enablePublishingMode">
                            <span class="icon">
                                <i class="fa fa-share" />
                            </span>
                            <span>{{ translate("page.admin_academy_results.publish_results") }}</span>
                        </button>
                    </div>
                </div>
                <div class="is-flex is-align-items-center" v-else>
                    <input
                        v-model="resultsName"
                        maxlength="100"
                        class="input mr-2"
                        type="text"
                        :placeholder="translate('page.admin_academy_results.name_results')"
                        ref="resultsNameInput"
                    />
                    <button class="mr-2 button is-success" @click="publishResults" :disabled="!canSendPublishingRequest">
                        <span class="icon">
                            <i class="fa fa-check" />
                        </span>
                        <span>{{ translate("common.save") }}</span>
                    </button>
                    <button class="button is-danger" @click="disablePublishingMode">
                        <span class="icon">
                            <i class="fa fa-times" />
                        </span>
                        <span>{{ translate("common.cancel") }}</span>
                    </button>
                </div>

                <ChosenRulesView :selected-rules="store2.selectedRules" class="mt-3" />
            </div>
        </div>

        <div class="columns is-multiline">
            <AcademyResultsSummary />
        </div>
    </div>
    <div v-show="!initDone" class="columns is-centered is-mobile">
        <LoadingSpinner2 class="has-added-margin" />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, nextTick } from "vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import AcademyResultsOptions from "./AcademyResultsOptions.vue";
    import { successToast } from "../Common/toasts";
    import competitorResultsApi, { CalculateAcademyResultsRequest } from "../Api/competitorResultsApi";
    import AcademyResultsSummary from "./AcademyResultsSummary.vue";
    import { TAB_PUBLISHED_RESULTS, useAcademyResultsStore } from "./academyResultsStore";
    import { ADD_RULE_MODAL, eventBus } from "./eventBus";
    import ChosenRulesView from "./CalculationRules/Viewing/ChosenRulesView.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import urls from "../Urls/urls";

    const store2 = useAcademyResultsStore();
    const initDone = ref(false);
    const resultsName = ref("");
    const resultsNameInput = ref<HTMLInputElement | null>(null);

    const eventId = computed(() => store2.eventId);
    const mergeAffiliations = computed(() => store2.mergeAffiliations);
    const showExAequoPlaces = computed(() => store2.showExAequoPlaces);

    /* RULE HANDLING */

    function openAddRuleModal() {
        eventBus.$emit(ADD_RULE_MODAL);
    }

    /* PUBLISHING */

    const canSendPublishingRequest = computed(() => {
        return resultsName.value.length > 0;
    });

    const publishingPossible = computed(() => {
        return store2.selectedRules.length;
    });

    const enablePublishingMode = () => {
        store2.publishingMode = true;

        nextTick(() => {
            if (resultsNameInput.value) {
                resultsNameInput.value.focus();
            }
        });
    };

    const disablePublishingMode = () => {
        resultsName.value = "";
        store2.publishingMode = false;
    };

    const publishResults = () => {
        return competitorResultsApi
            .publishAcademyResults(eventId.value, {
                name: resultsName.value,
                criteria: {
                    selectedRules: store2.selectedRules,
                    mergeAffiliations: mergeAffiliations.value,
                    showExAequoPlaces: showExAequoPlaces.value,
                } satisfies CalculateAcademyResultsRequest,
            })
            .then(() => {
                disablePublishingMode();
                resultsName.value = "";
                successToast(translate("page.admin_academy_results.published"));
                store2.view = TAB_PUBLISHED_RESULTS;
            })
            .catch(handleError);
    };

    onMounted(() => {
        resultsName.value = "";
        store2.loadFromLocalStorage();

        return store2
            .fetchAcademyResults()
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    });

    // REFRESHING

    const loadingInProgress = ref(false);

    const refreshPossible = computed(() => {
        return store2.selectedRules.length > 0;
    });

    function refreshResults() {
        loadingInProgress.value = true;
        return store2
            .fetchAcademyResults()
            .catch(handleError)
            .finally(() => {
                loadingInProgress.value = false;
            });
    }

    // DOWNLOAD IN CSV

    function getExportCsvUrl() {
        const url = urls.eventAdmin().downloadAcademyResultsAsCsv(eventId.value, {
            selectedRules: store2.selectedRules,
            mergeAffiliations: mergeAffiliations.value,
            showExAequoPlaces: showExAequoPlaces.value,
        });
        return url;
    }
</script>

<style scoped></style>
